import { useEffect, useMemo, useRef, useState } from "react";
import { tg, errorTg, colorAvatar, iniciales } from "../../lib/telegram";
import useModalFoco from "../../hooks/useModalFoco";

const ROL = { asistente: "asistente", bilingue: "bilingüe" };
const kc = (chat, persona) => `${chat}|${persona}`;

/**
 * «Quién ve qué» (solo el dueño de la cuenta): columnas = personas, filas = chats, «Ver» y «Escribir» por celda.
 * Los chats «Solo yo» salen aparte y NUNCA se pueden compartir. Los cambios se guardan celda por celda; el servidor decide.
 */
export default function QuienVeQue({ onCerrar }) {
  const [datos, setDatos] = useState(null);
  const [orig, setOrig] = useState({});
  const [borrador, setBorrador] = useState({});
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState("");
  const vivo = useRef(true);
  const modalRef = useRef(null);
  useModalFoco(modalRef, onCerrar);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const cargar = async () => {
    try {
      const r = await tg.get("/grants");
      if (!vivo.current) return;
      const m = {};
      (r.data?.grants || []).forEach((g) => { m[kc(g.chat_id, g.grantee_user_id)] = { v: !!g.can_view, w: !!g.can_write }; });
      setDatos(r.data || {}); setOrig(m); setBorrador(m); setError("");
    } catch (e) { if (vivo.current) setError(errorTg(e)); }
  };
  useEffect(() => { cargar(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const celda = (chat, persona) => borrador[kc(chat, persona)] || { v: false, w: false };
  const poner = (chat, persona, parte) => { setOk(""); setBorrador((b) => ({ ...b, [kc(chat, persona)]: { ...celda(chat, persona), ...parte } })); };
  const alternarVer = (c, p) => { const x = celda(c, p); poner(c, p, x.v ? { v: false, w: false } : { v: true }); };
  const alternarEscribir = (c, p) => { const x = celda(c, p); poner(c, p, x.w ? { w: false } : { v: true, w: true }); };

  const cambios = useMemo(() => {
    const claves = new Set([...Object.keys(orig), ...Object.keys(borrador)]);
    return [...claves].filter((k) => {
      const a = orig[k] || { v: false, w: false };
      const b = borrador[k] || { v: false, w: false };
      return a.v !== b.v || a.w !== b.w;
    });
  }, [orig, borrador]);

  const guardar = async () => {
    if (guardando || !cambios.length) return;
    setGuardando(true); setError(""); setOk("");
    const hechos = { ...orig };
    let fallos = 0;
    let ultimoError = "";
    for (const k of cambios) {
      const [chat, persona] = k.split("|");
      const b = borrador[k] || { v: false, w: false };
      try {
        await tg.put("/grants", { chat_id: chat, grantee_user_id: persona, can_view: b.v, can_write: b.w });
        hechos[k] = b;
      } catch (e) { fallos += 1; ultimoError = errorTg(e); }
    }
    if (!vivo.current) return;
    setOrig(hechos); setGuardando(false);
    if (fallos) setError(`${fallos} cambio(s) no se guardaron: ${ultimoError}`);
    else setOk("Cambios guardados. Surten efecto al instante.");
  };

  const personas = datos?.people || [];
  const todos = datos?.chats || [];
  const normales = todos.filter((c) => !c.solo_yo);
  const soloYo = todos.filter((c) => c.solo_yo);

  return (
    <div className="tg-modal-fondo" role="dialog" aria-modal="true" aria-label="Quién ve qué" data-testid="tg-quien" ref={modalRef}>
      <div className="tg-qm">
        <div className="tg-fila">
          <div><div className="tg-ttl">Quién ve qué</div><div className="tg-hint">{personas.length} personas · {todos.length} chats</div></div>
          <span className="tg-sp" />
          <span className="tg-pill am">EMPLEADOS: CHAT POR CHAT</span>
          <button type="button" className="tg-btn sm" aria-label="Cerrar" data-testid="tg-quien-cerrar" onClick={onCerrar}>✕</button>
        </div>
        {datos && datos.send_enabled === false && <div className="tg-aviso" data-testid="tg-quien-envio-apagado">El envío está apagado en el servidor: aunque marques «Escribir», por ahora nadie podrá enviar.</div>}
        {error && <div className="tg-err" role="alert" data-testid="tg-quien-error">{error}</div>}
        {ok && <div className="tg-aviso ok" role="status" data-testid="tg-quien-ok">{ok}</div>}
        {!datos && !error && <div className="tg-nd">Cargando…</div>}
        {datos && !personas.length && <div className="tg-estado" data-testid="tg-quien-sin-personas"><b>Aún no hay personas</b><span>Aquí aparecen los asistentes y bilingües de tu equipo.</span></div>}
        {datos && personas.length > 0 && (
          <div className="tg-qg" style={{ gridTemplateColumns: `240px repeat(${personas.length}, minmax(150px, 1fr))` }} data-testid="tg-quien-rejilla">
            <div />
            {personas.map((p) => (
              <div className="tg-qh" key={p.id} data-testid="tg-persona"><span className="tg-qpa" style={{ background: colorAvatar(p.id) }}>{iniciales(p.name)}</span><b>{p.name}</b><span className="tg-pill">{ROL[p.role] || p.role}</span></div>
            ))}
            {normales.map((c) => (
              <div className="tg-qfila" key={c.chat_id} data-testid="tg-quien-fila" style={{ display: "contents" }}>
                <div className="tg-qc"><span className="tg-av" style={{ width: 40, height: 40, fontSize: 13, background: colorAvatar(c.chat_id) }}>{iniciales(c.title)}</span><div><b>{c.title}</b><small>{{ groups: "grupo", personal: "personal", bots: "bot" }[c.folder] || ""}</small></div></div>
                {personas.map((p) => {
                  const x = celda(c.chat_id, p.id);
                  return (
                    <div className="tg-qx" key={p.id}>
                      <div className="tg-sg">
                        <button type="button" className={x.v ? "v" : ""} aria-pressed={x.v} aria-label={`Ver · ${p.name} · ${c.title}`} data-testid="tg-celda-ver" onClick={() => alternarVer(c.chat_id, p.id)}>{x.v ? "✓ Ver" : "Ver"}</button>
                        <button type="button" className={x.w ? "w" : ""} aria-pressed={x.w} aria-label={`Escribir · ${p.name} · ${c.title}`} data-testid="tg-celda-escribir" onClick={() => alternarEscribir(c.chat_id, p.id)}>{x.w ? "✎ Escribir" : "Escribir"}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {soloYo.map((c) => (
              <div className="tg-qlock" key={c.chat_id} data-testid="tg-quien-soloyo">
                <span className="tg-av" style={{ width: 40, height: 40, background: "#8E6FD8" }}>🔒</span>
                <div><b>{c.title}</b><div className="tg-hint">Solo yo · con clave · nunca se comparte con nadie</div></div><span className="tg-sp" /><span className="tg-pill lock">PRIVADO</span>
              </div>
            ))}
          </div>
        )}
        <div className="tg-fila">
          <span className="tg-hint">Si apagas «Ver», el chat desaparece de la pantalla de esa persona al momento. Lo que escriban sale firmado con su nombre y queda registrado quién lo escribió.</span>
          <span className="tg-sp" />
          <button type="button" className="tg-btn" data-testid="tg-quien-cancelar" onClick={onCerrar}>Cerrar</button>
          <button type="button" className="tg-btn pri" data-testid="tg-quien-guardar" disabled={guardando || !cambios.length} onClick={guardar}>{guardando ? "Guardando…" : "Guardar cambios"}</button>
        </div>
      </div>
    </div>
  );
}
